// frontend/src/components/common/ui/LoadingState.tsx
// Unified loading state component for consistent loading experiences across the application

import React from 'react';
import { Loader2, Clock, Download, Upload, RefreshCw } from 'lucide-react';

// Utility function to combine class names
const cn = (...classes: (string | undefined | null | boolean)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export type LoadingVariant = 'spinner' | 'dots' | 'pulse' | 'skeleton' | 'progress';
export type LoadingSize = 'sm' | 'md' | 'lg' | 'xl';
export type LoadingType = 'default' | 'processing' | 'uploading' | 'downloading' | 'refreshing';

export interface LoadingStateProps {
  variant?: LoadingVariant;
  size?: LoadingSize;
  type?: LoadingType;
  title?: string;
  message?: string;
  progress?: number; // 0-100 for progress bars
  showProgress?: boolean;
  className?: string;
  centerContent?: boolean;
  fullHeight?: boolean;
  overlay?: boolean;
  transparent?: boolean;
  children?: React.ReactNode;
}

// Type configurations
const typeConfig = {
  default: {
    icon: Loader2,
    title: 'Loading...',
    message: 'Please wait while we load your content.',
  },
  processing: {
    icon: Clock,
    title: 'Processing...',
    message: 'Please wait while we process your request.',
  },
  uploading: {
    icon: Upload,
    title: 'Uploading...',
    message: 'Please wait while we upload your files.',
  },
  downloading: {
    icon: Download,
    title: 'Downloading...',
    message: 'Please wait while we download your files.',
  },
  refreshing: {
    icon: RefreshCw,
    title: 'Refreshing...',
    message: 'Please wait while we refresh your data.',
  },
};

// Size configurations
const sizeConfig = {
  sm: {
    icon: 'h-6 w-6',
    title: 'text-base',
    message: 'text-sm',
    container: 'p-4',
    spacing: 'space-y-2',
    skeleton: {
      height: 'h-3',
      avatar: 'h-8 w-8',
      button: 'h-8 w-16',
    },
  },
  md: {
    icon: 'h-8 w-8',
    title: 'text-lg',
    message: 'text-base',
    container: 'p-6',
    spacing: 'space-y-3',
    skeleton: {
      height: 'h-4',
      avatar: 'h-10 w-10',
      button: 'h-9 w-20',
    },
  },
  lg: {
    icon: 'h-12 w-12',
    title: 'text-xl',
    message: 'text-lg',
    container: 'p-8',
    spacing: 'space-y-4',
    skeleton: {
      height: 'h-5',
      avatar: 'h-12 w-12',
      button: 'h-10 w-24',
    },
  },
  xl: {
    icon: 'h-16 w-16',
    title: 'text-2xl',
    message: 'text-xl',
    container: 'p-12',
    spacing: 'space-y-6',
    skeleton: {
      height: 'h-6',
      avatar: 'h-16 w-16',
      button: 'h-12 w-32',
    },
  },
};

// Loading variants
const LoadingVariants = {
  spinner: ({ size, type }: { size: LoadingSize; type: LoadingType }) => {
    const config = typeConfig[type];
    const IconComponent = config.icon;
    return (
      <IconComponent 
        className={cn(
          sizeConfig[size].icon, 
          'text-blue-600 animate-spin'
        )} 
      />
    );
  },
  
  dots: ({ size }: { size: LoadingSize }) => (
    <div className="flex space-x-1">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'rounded-full bg-blue-600 animate-bounce',
            size === 'sm' && 'w-2 h-2',
            size === 'md' && 'w-3 h-3',
            size === 'lg' && 'w-4 h-4',
            size === 'xl' && 'w-5 h-5'
          )}
          style={{
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  ),
  
  pulse: ({ size }: { size: LoadingSize }) => (
    <div
      className={cn(
        'rounded-full bg-blue-600 animate-pulse opacity-75',
        sizeConfig[size].icon
      )}
    />
  ),
  
  skeleton: ({ size }: { size: LoadingSize }) => {
    const skeletonStyles = sizeConfig[size].skeleton;
    return (
      <div className="animate-pulse space-y-3">
        {/* Header skeleton */}
        <div className="flex items-center space-x-3">
          <div className={cn('bg-gray-200 rounded-full', skeletonStyles.avatar)} />
          <div className="flex-1 space-y-2">
            <div className={cn('bg-gray-200 rounded w-3/4', skeletonStyles.height)} />
            <div className={cn('bg-gray-200 rounded w-1/2', skeletonStyles.height)} />
          </div>
        </div>
        
        {/* Content skeleton */}
        <div className="space-y-2">
          <div className={cn('bg-gray-200 rounded w-full', skeletonStyles.height)} />
          <div className={cn('bg-gray-200 rounded w-5/6', skeletonStyles.height)} />
          <div className={cn('bg-gray-200 rounded w-4/6', skeletonStyles.height)} />
        </div>
        
        {/* Actions skeleton */}
        <div className="flex space-x-3">
          <div className={cn('bg-gray-200 rounded', skeletonStyles.button)} />
          <div className={cn('bg-gray-200 rounded', skeletonStyles.button)} />
        </div>
      </div>
    );
  },
  
  progress: ({ progress }: { progress: number }) => (
    <div className="w-full">
      <div className="bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
        />
      </div>
    </div>
  ),
};

const LoadingState: React.FC<LoadingStateProps> = ({
  variant = 'spinner',
  size = 'md',
  type = 'default',
  title,
  message,
  progress = 0,
  showProgress = false,
  className,
  centerContent = true,
  fullHeight = false,
  overlay = false,
  transparent = false,
  children,
}) => {
  const config = typeConfig[type];
  const sizeStyles = sizeConfig[size];
  
  const effectiveTitle = title || config.title;
  const effectiveMessage = message || config.message;
  
  const LoadingComponent = LoadingVariants[variant];
  
  const loadingElement = (
    <LoadingComponent 
      size={size} 
      type={type} 
      progress={showProgress ? progress : 0} 
    />
  );
  
  const content = (
    <div className={cn(
      sizeStyles.spacing,
      centerContent && 'text-center'
    )}>
      <div className={cn(centerContent && 'flex justify-center')}>
        {loadingElement}
      </div>
      
      {effectiveTitle && (
        <h3 className={cn(sizeStyles.title, 'font-medium text-gray-900')}>
          {effectiveTitle}
        </h3>
      )}
      
      {effectiveMessage && (
        <p className={cn(sizeStyles.message, 'text-gray-600')}>
          {effectiveMessage}
        </p>
      )}
      
      {showProgress && variant !== 'progress' && (
        <div className="mt-4">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <LoadingVariants.progress progress={progress} />
        </div>
      )}
      
      {children && (
        <div className="mt-4">
          {children}
        </div>
      )}
    </div>
  );

  // Full height loading
  if (fullHeight) {
    const bgClasses = transparent 
      ? 'bg-white/80 backdrop-blur-sm' 
      : 'bg-white';
    
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center',
        bgClasses,
        className
      )}>
        <div className={sizeStyles.container}>
          {content}
        </div>
      </div>
    );
  }
  
  // Overlay loading
  if (overlay) {
    const bgClasses = transparent 
      ? 'bg-white/80 backdrop-blur-sm' 
      : 'bg-white';
    
    return (
      <div className={cn(
        'absolute inset-0 z-40 flex items-center justify-center',
        bgClasses,
        className
      )}>
        <div className={sizeStyles.container}>
          {content}
        </div>
      </div>
    );
  }
  
  // Card loading
  return (
    <div className={cn(
      'bg-white rounded-lg shadow-sm border',
      sizeStyles.container,
      centerContent && 'flex items-center justify-center',
      className
    )}>
      {content}
    </div>
  );
};

// Loading card with skeleton
interface LoadingCardProps {
  isLoading: boolean;
  children: React.ReactNode;
  loadingProps?: Partial<LoadingStateProps>;
  className?: string;
  minHeight?: string;
}

export const LoadingCard: React.FC<LoadingCardProps> = ({
  isLoading,
  children,
  loadingProps = {},
  className,
  minHeight = 'min-h-32',
}) => {
  if (isLoading) {
    return (
      <div className={cn('relative', minHeight, className)}>
        <LoadingState
          variant="skeleton"
          {...loadingProps}
          overlay
          transparent
        />
      </div>
    );
  }
  
  return <>{children}</>;
};

// Loading table rows
interface LoadingTableRowsProps {
  columns: number;
  rows?: number;
  size?: LoadingSize;
  className?: string;
}

export const LoadingTableRows: React.FC<LoadingTableRowsProps> = ({
  columns,
  rows = 3,
  size = 'md',
  className,
}) => {
  const skeletonHeight = sizeConfig[size].skeleton.height;
  
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex} className={className}>
          {Array.from({ length: columns }, (_, colIndex) => (
            <td key={colIndex} className="px-6 py-4">
              <div className="animate-pulse">
                <div className={cn('bg-gray-200 rounded w-full', skeletonHeight)} />
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

// Loading list items
interface LoadingListItemsProps {
  items?: number;
  showAvatar?: boolean;
  showActions?: boolean;
  size?: LoadingSize;
  className?: string;
}

export const LoadingListItems: React.FC<LoadingListItemsProps> = ({
  items = 3,
  showAvatar = false,
  showActions = false,
  size = 'md',
  className,
}) => {
  const skeletonStyles = sizeConfig[size].skeleton;
  
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: items }, (_, index) => (
        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
          {showAvatar && (
            <div className="animate-pulse">
              <div className={cn('bg-gray-200 rounded-full', skeletonStyles.avatar)} />
            </div>
          )}
          <div className="flex-1 animate-pulse space-y-2">
            <div className={cn('bg-gray-200 rounded w-3/4', skeletonStyles.height)} />
            <div className={cn('bg-gray-200 rounded w-1/2', skeletonStyles.height)} />
          </div>
          {showActions && (
            <div className="animate-pulse">
              <div className={cn('bg-gray-200 rounded', skeletonStyles.button)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Page loading wrapper
interface PageLoadingProps {
  isLoading: boolean;
  type?: 'table' | 'list' | 'form' | 'cards' | 'profile';
  children: React.ReactNode;
  loadingProps?: Partial<LoadingStateProps>;
  className?: string;
}

export const PageLoading: React.FC<PageLoadingProps> = ({
  isLoading,
  type = 'cards',
  children,
  loadingProps = {},
  className,
}) => {
  if (isLoading) {
    const renderLoadingContent = () => {
      switch (type) {
        case 'table':
          return (
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="p-4 border-b">
                <div className="animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/4" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {Array.from({ length: 5 }, (_, i) => (
                        <th key={i} className="px-6 py-3">
                          <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-20" />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <LoadingTableRows columns={5} rows={5} />
                  </tbody>
                </table>
              </div>
            </div>
          );
        
        case 'list':
          return <LoadingListItems items={5} showAvatar showActions />;
        
        case 'form':
          return (
            <LoadingState
              variant="skeleton"
              size="lg"
              title="Loading Form"
              message="Please wait while we prepare the form for you."
              {...loadingProps}
            />
          );
        
        case 'profile':
          return (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="animate-pulse space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="h-20 w-20 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-6 bg-gray-200 rounded w-1/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
                <div className="space-y-3">
                  {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className="flex">
                      <div className="h-4 bg-gray-200 rounded w-1/4 mr-4" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        
        case 'cards':
        default:
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }, (_, i) => (
                <LoadingState
                  key={i}
                  variant="skeleton"
                  size="md"
                  {...loadingProps}
                />
              ))}
            </div>
          );
      }
    };

    return (
      <div className={className}>
        {renderLoadingContent()}
      </div>
    );
  }
  
  return <>{children}</>;
};

// Loading button
interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  loadingText?: string;
  loadingType?: LoadingType;
  size?: LoadingSize;
  children: React.ReactNode;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  isLoading = false,
  loadingText,
  loadingType = 'processing',
  size = 'md',
  children,
  disabled,
  className,
  ...props
}) => {
  const config = typeConfig[loadingType];
  const IconComponent = config.icon;
  
  const iconSize = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
  
  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      className={cn(
        'relative flex items-center justify-center transition-opacity',
        isLoading && 'cursor-not-allowed opacity-75',
        className
      )}
    >
      {isLoading && (
        <IconComponent className={cn(iconSize, 'mr-2 animate-spin')} />
      )}
      {isLoading && loadingText ? loadingText : children}
    </button>
  );
};

// Loading overlay
interface LoadingOverlayProps {
  isVisible: boolean;
  children?: React.ReactNode;
  className?: string;
  loadingProps?: Partial<LoadingStateProps>;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  children,
  className,
  loadingProps = {},
}) => {
  if (!isVisible) return null;
  
  return (
    <div className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'flex items-center justify-center',
      className
    )}>
      <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-sm w-full">
        <LoadingState
          size="lg"
          centerContent
          {...loadingProps}
        />
        {children}
      </div>
    </div>
  );
};

export default LoadingState;